import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  Button,
  FormControl,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from '@mui/material';
import {
  getJudges,
  assignJudgeToMultipleCategories,
} from '../../services/judgeCat.service';
import { getCategories } from '../../services/categories.services';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { unassignJudgeFromCategories } from '../../services/judgeCat.service';
import { arrayRemove, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';

interface Judge {
  id: string;
  firstName: string;
  lastName: string;
  categories?: string[];
  assignedCategory?: string;
}

interface Category {
  id: string;
  name: string;
  linkedCategoryIds?: string[];
}
const getAssignableCategories = (
  allCategories: Category[],
  preferredIds: string[]
): Category[] => {
  const resultMap = new Map<string, Category>();

  preferredIds.forEach((id) => {
    const mainCat = allCategories.find((cat) => cat.id === id);
    if (mainCat) {
      resultMap.set(mainCat.id, mainCat);
      mainCat.linkedCategoryIds?.forEach((linkedId) => {
        const linkedCat = allCategories.find((c) => c.id === linkedId);
        if (linkedCat) resultMap.set(linkedCat.id, linkedCat);
      });
    }
  });

  return Array.from(resultMap.values());
};

const ProjectAssignment: React.FC = () => {
  const { authStatus } = useAuth();
  const [judges, setJudges] = useState<Judge[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [assignments, setAssignments] = useState<{ [id: string]: string[] }>(
    {}
  );

  useEffect(() => {
    const fetchData = async () => {
      const judgesList = (await getJudges()) as Judge[];
      const regionId = authStatus.metadata?.regionId;
      const categoryList = await getCategories(regionId);
      setJudges(judgesList);
      setCategories(categoryList);

      const initial: { [id: string]: string[] } = {};
      judgesList.forEach((j) => {
        if (Array.isArray(j.assignedCategory)) {
          initial[j.id] = j.assignedCategory;
        } else if (j.assignedCategory) {
          initial[j.id] = [j.assignedCategory];
        }
      });

      setAssignments(initial);
    };

    fetchData();
  }, [authStatus.metadata?.regionId]);

  const handleAssign = async (judgeId: string) => {
    const selectedCategories = assignments[judgeId] || [];
    const judge = judges.find((j) => j.id === judgeId);

    console.log(`Assigning categories to judge: ${judgeId}`);
    console.log('Selected categories:', selectedCategories);
    console.log('Judge object:', judge);

    const assignable = getAssignableCategories(
      categories,
      judge?.categories ?? []
    );
    console.log(
      'Assignable categories:',
      assignable.map((c) => c.id)
    );

    const allValid = selectedCategories.every((id) =>
      assignable.some((cat) => cat.id === id)
    );

    if (!selectedCategories.length || !allValid) {
      alert(
        'Invalid assignment: One or more categories are not valid for this judge.'
      );
      return;
    }

    // Send to backend — adjust `assignJudgeToCategory` to accept multiple categories
    try {
      for (const categoryId of selectedCategories) {
        console.log(
          `Calling assignJudgeToMultipleCategories(${judgeId}, [${categoryId}])`
        );
        await assignJudgeToMultipleCategories(judgeId, [categoryId]);
      }
      alert('Assignments updated!');
    } catch (err) {
      console.error('Error assigning categories:', err);
      alert('Failed to update assignments.');
    }
  };

  const handleUnassign = async (judgeId: string) => {
    const selectedCategories = assignments[judgeId] || [];

    if (!selectedCategories.length) {
      alert('Please select one or more categories to unassign.');
      return;
    }

    try {
      const results = await Promise.all(
        selectedCategories.map(async (categoryId) => {
          const categoryRef = doc(db, 'categories', categoryId);
          const snapshot = await getDoc(categoryRef);

          if (!snapshot.exists()) return { id: categoryId, assigned: false };

          const data = snapshot.data();
          const isAssigned = data?.assignedJudgeIds?.includes(judgeId);

          if (!isAssigned) {
            return { id: categoryId, assigned: false };
          }

          await updateDoc(categoryRef, {
            assignedJudgeIds: arrayRemove(judgeId),
          });

          return { id: categoryId, assigned: true };
        })
      );

      const unassigned = results
        .filter((r) => r.assigned)
        .map((r) => {
          const cat = categories.find((c) => c.id === r.id);
          return cat ? cat.name : r.id;
        });

      const notAssigned = results
        .filter((r) => !r.assigned)
        .map((r) => {
          const cat = categories.find((c) => c.id === r.id);
          return cat ? cat.name : r.id;
        });

      if (unassigned.length) {
        alert(`Successfully unassigned from: ${unassigned.join(', ')}`);
      }

      if (notAssigned.length) {
        alert(
          `Judge was not assigned to these categories: ${notAssigned.join(', ')}`
        );
      }
    } catch (err) {
      console.error('Error unassigning categories:', err);
      alert('Failed to unassign categories.');
    }
  };

  return (
    <Box p={4}>
      <Typography variant="h5" gutterBottom>
        Judge Category Assignment
      </Typography>

      <Paper sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Judge Name</TableCell>
              <TableCell>Preferred Categories</TableCell>
              <TableCell>All Possible Categories</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {judges.map((judge) => {
              const preferredCats = judge.categories ?? [];
              const assignableCats = getAssignableCategories(
                categories,
                preferredCats
              );

              return (
                <TableRow key={judge.id}>
                  <TableCell>
                    {judge.firstName} {judge.lastName}
                  </TableCell>
                  <TableCell>
                    {preferredCats.length
                      ? preferredCats
                          .map((id) => {
                            const cat = categories.find((c) => c.id === id);
                            return cat ? cat.name : null;
                          })
                          .filter(Boolean)
                          .join(', ')
                      : 'None'}
                  </TableCell>
                  <TableCell>
                    <FormControl fullWidth>
                      <Select
                        multiple
                        displayEmpty
                        value={assignments[judge.id] || []}
                        onChange={(e) =>
                          setAssignments((prev) => ({
                            ...prev,
                            [judge.id]: e.target.value as string[],
                          }))
                        }
                        input={<OutlinedInput />}
                        renderValue={(selected) =>
                          selected
                            .map(
                              (id) =>
                                assignableCats.find((cat) => cat.id === id)
                                  ?.name || ''
                            )
                            .join(', ')
                        }
                      >
                        {assignableCats.map((cat) => (
                          <MenuItem key={cat.id} value={cat.id}>
                            <Checkbox
                              checked={
                                assignments[judge.id]?.includes(cat.id) || false
                              }
                            />
                            <ListItemText primary={cat.name} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleAssign(judge.id)}
                    >
                      Assign
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      sx={{ ml: 1 }}
                      onClick={() => handleUnassign(judge.id)}
                    >
                      Unassign
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default ProjectAssignment;
